import React, { useState } from 'react';
import { Card, Tabs, Breadcrumb, Space, Button, App } from 'antd';
import { Link } from 'react-router-dom';
import { HomeOutlined, SettingOutlined } from '@ant-design/icons';
import { SubscriptionList } from '../components/list/SubscriptionList';
import  DynamicFormConfigModal  from "../components/forms/DynamicFormConfigModal";
import { useTheme } from '@/components/common/ThemeProvider';
import { showSuccessNotification, showErrorNotification } from '@/shared/utils/notify';
import { ActionFlowSubscriptionDTO } from '../../api';
import {useManageSubscription} from "@/modules/action-flow/config/hooks/use-action-flow";

const { TabPane } = Tabs;

export const SubscriptionManagement: React.FC = () => {
    const { dark } = useTheme();
    const [activeTab, setActiveTab] = useState('1');
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState<ActionFlowSubscriptionDTO | null>(null);
    const { setFormConfig } = useManageSubscription();

    const handleConfigSubmit = async (config: any) => {
        if (!selectedSubscription?.domain || !selectedSubscription?.subscriptionId) {
            showErrorNotification('Invalid subscription selection');
            return;
        }

        try {
            await setFormConfig({
                domain: selectedSubscription.domain,
                subscriptionId: selectedSubscription.subscriptionId,
                data: JSON.stringify(config)
            });
            showSuccessNotification('Form configuration updated successfully');
            setIsConfigModalOpen(false);
            setSelectedSubscription(null);
        } catch (error) {
            showErrorNotification('Failed to update form configuration');
        }
    };

    return (
        <App>
            <div className="p-6">
                <Space direction="vertical" size="large" className="w-full">
                {/* Breadcrumb Navigation */}
                <Breadcrumb 
                    className="mb-4"
                    items={[
                        {
                            title: (
                                <Link to="/">
                                    <HomeOutlined /> Home
                                </Link>
                            )
                        },
                        {
                            title: (
                                <Link to="/admin">Administration</Link>
                            )
                        },
                        {
                            title: 'Action Flow Subscriptions'
                        }
                    ]}
                />

                {/* Main Content */}
                <Card
                    className={dark ? 'bg-gray-800' : ''}
                    bordered={false}
                >
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        className={dark ? 'text-white' : ''}
                        tabBarExtraContent={
                            <Space>
                                <Button
                                    icon={<SettingOutlined />}
                                    onClick={() => setIsConfigModalOpen(true)}
                                    disabled={!selectedSubscription}
                                >
                                    Configure Forms
                                </Button>
                            </Space>
                        }
                    >
                        <TabPane tab="Active Subscriptions" key="1">
                            <SubscriptionList
                                onSubscriptionSelect={setSelectedSubscription}
                                selectedSubscription={selectedSubscription}
                            />
                        </TabPane>
                        <TabPane tab="Archived Subscriptions" key="2">
                            <SubscriptionList
                                archived
                                onSubscriptionSelect={setSelectedSubscription}
                                selectedSubscription={selectedSubscription}
                            />
                        </TabPane>
                    </Tabs>
                </Card>

                {/* Dynamic Form Configuration Modal */}
                <DynamicFormConfigModal
                    open={isConfigModalOpen}
                    onCancel={() => {
                        setIsConfigModalOpen(false);
                        setSelectedSubscription(null);
                    }}
                    onSubmit={handleConfigSubmit}
                    initialConfig={selectedSubscription?.dynamicFormConfig}
                    subscription={selectedSubscription}
                />
                </Space>
            </div>
        </App>
    );
};


export default SubscriptionManagement;